import { useEffect, useState } from 'react';

import {
  ArrowButton, Button, ColumnSortEnum, Modal, Table, TableMetadata,
  TableStateWithFilters,
  TextField, Typography, useTable
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as DownloadIcon } from "@king-ict/eupisi-ui/components/Icon/Download.svg";
import { ExportTypeEnum } from '@king-ict/eupisi-ui/models';
import { nameof } from '@king-ict/eupisi-ui/utils';
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { deleteProgramType, getProgramTypes, exportProgramTypes } from '../../api/nisooApi';
import { useDidUpdateEffect, useInput } from "../../hooks";
import { CodeBookExtended, CodeBookSearch } from '../../models/CodeBook';
import { useGetUserSessionQuery } from '../../queries/userQuery';
import { codebookRouteProgramType } from '../../routes/routes';
import styles from "./RequestCodeBook.module.scss";

export default function CodeBookProgramType(): JSX.Element {
  const storageKey = 'CodeBookProgramTypeSearchTableState';
  const defaultPageSaze = 10;

  const createInitialTableState = (): TableStateWithFilters<CodeBookSearch> => {
    const sessionStorageTableState = sessionStorage.getItem(storageKey);
    if (sessionStorageTableState === null) {
      return {
        name: "",
        page: 1,
        pageSize: defaultPageSaze,
        sortBy: 1,
        order: ColumnSortEnum.ASC
      };
    }

    var result = JSON.parse(sessionStorageTableState) as TableStateWithFilters<CodeBookSearch>;
    return result;
  }

  const createInitialSearch = (initialTableState: TableStateWithFilters<CodeBookSearch>): CodeBookSearch => {
    return {
      name: initialTableState.name
    }
  }

  const history = useHistory();
  const { t } = useTranslation();
  const { handleChange } = useInput();

  const { data: userSession } = useGetUserSessionQuery();
  const [initialTableState] = useState<TableStateWithFilters<CodeBookSearch>>(() => createInitialTableState());
  const [searchFilter, setSearchFilter] = useState<CodeBookSearch>(() => createInitialSearch(initialTableState));
  const [tableProgramTypes, setTableProgramTypes] = useState<CodeBookExtended[]>([]);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const [programTypeToDelete, setProgramTypeToDelete] = useState<CodeBookExtended>()

  const [programTypes, setProgramTypes] = useState<CodeBookExtended[]>([]);
  const [itemCount, setItemCount] = useState(programTypes?.length ?? 0);

  const callDataFetch = async () => {
    var result = await getProgramTypes();
    setProgramTypes(result);
    setItemCount(result?.length ?? 0);
  }

  const tableMetadata: TableMetadata<CodeBookExtended> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "name",
        header: t("codeBook.programType"),
        accessor: "name",
        sortBy: 1
      },
      {
        columnKey: "canDelete",
        header: "",
        accessor: (item) => {
          return (
            <Typography color="primary" className={styles.clearFilters + " ml-3"} onClick={() => deleteItem(item)}>
              {item.canDelete ? t("common.remove") : ""}
            </Typography>
          )
        }
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item) => {
          return (<div className={"text-right"}>
            <ArrowButton ariaLabel={t("codeBook.programTypes.goto")}
              onClick={() => { navigateToDetails(item) }}
            />
          </div>)
        }
      }
    ],
    rowToolTip: () => undefined
  };

  var [tableProps, tableState, setFilters] = useTable<CodeBookSearch>(
    {
      metadata: tableMetadata,
      data: tableProgramTypes,
      pagination: true,
      paginationPage: initialTableState.page as any,
      paginationPageSize: initialTableState.pageSize as any,
      totalItems: itemCount,
      defaultSort: { order: initialTableState.order, sortBy: initialTableState.sortBy }
    }
  );

  const search = async () => {
    setFilters(searchFilter);
  };

  const clearFilters = async () => {
    const clearSearchFilter = { name: "" }
    setSearchFilter(clearSearchFilter);
    sessionStorage.setItem(storageKey, JSON.stringify({
      name: "",
      sortBy: tableState.sortBy ?? 1,
      order: tableState.order ?? ColumnSortEnum.ASC,
      page: tableState.page ?? 1,
      pageSize: tableState.pageSize ?? defaultPageSaze
    }));
    setTableProgramTypes(programTypes);
    setFilters(clearSearchFilter);
  };

  useEffect(() => {
    callDataFetch();
  }, [tableState, userSession]);

  useEffect(() => {
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!programTypes) {
      return;
    }
    const filteredProgramTypes = () => {
      let filter = tableState.name ? tableState.name?.toLowerCase() : "";

      let filteredProgramTypes = programTypes
        .filter((item) => item.name.toLowerCase()
          .includes(filter))
        .sort((x, y) => tableState.order === ColumnSortEnum.ASC ? x.name.localeCompare(y.name) : (-1) * x.name.localeCompare(y.name));

      setItemCount(filteredProgramTypes.length);
      let pageSize = (tableProps.paginationProps?.pageSize ?? 0);
      let startIndex = ((tableProps.paginationProps?.page ?? 1) - 1) * pageSize;
      let endIndex = startIndex + pageSize;
      return filteredProgramTypes.slice(startIndex, endIndex);
    }
    setTableProgramTypes(filteredProgramTypes());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState, programTypes]);

  useDidUpdateEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify({
      ...searchFilter,
      sortBy: tableState.sortBy ?? 1,
      order: tableState.order ?? ColumnSortEnum.ASC,
      page: tableState.page ?? 1,
      pageSize: tableState.pageSize ?? defaultPageSaze
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState])

  const navigateToDetails = (item: CodeBookExtended) => {
    history.push(
      codebookRouteProgramType(),
      {
        page: "details",
        programType: item
      }
    );
  }

  const deleteItem = async (item: CodeBookExtended) => {
    if (!item.canDelete) {
      return;
    }

    setProgramTypeToDelete(item);
    setOpenConfirmModal(true);
  }

  const handleCancelConfirmModal = async () => {
    setOpenConfirmModal(false);
  }

  const handleConfirmedConfirmModal = async () => {
    setOpenConfirmModal(false);
    await deleteProgramType(programTypeToDelete!.id!);
    await callDataFetch();
  }

  const download = async (exportType: ExportTypeEnum) => {
    exportProgramTypes(exportType);
  }

  return (
    <>
      <Row className="mt-5">
        <Col md={6}>
          <h3>{t("codeBook.programType")}</h3>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col md={6}>
          <TextField
            label={t("codeBook.programType")}
            name={nameof<typeof searchFilter>("name")}
            value={searchFilter.name}
            maxLength={50}
            onChange={handleChange(setSearchFilter)}
          />
        </Col>
        <Col md={6} className="d-flex justify-content-end align-items-center">
          <Button onClick={search}>
            {t("common.search")}
          </Button>
          <Typography color="primary" className={styles.clearFilters + " ml-5"} onClick={clearFilters}>
            {t("common.clearFilters")}
          </Typography>
        </Col>
      </Row>
      <Table  {...tableProps} />
      <Row className="mt-5">
        <Col md={12} className="d-flex justify-content-end align-items-center">
          <Button
            className={styles.exportButton}
            outline
            color={"primary"}
            icon={<DownloadIcon />}
            onClick={() => download(ExportTypeEnum.Csv)}
          >
            {t("common.downloadDocumentExtension", { extension: ".csv" })}
          </Button>
          <Button
            className={styles.exportButton}
            outline
            color={"primary"}
            icon={<DownloadIcon />}
            onClick={() => download(ExportTypeEnum.Excel)}
          >
            {t("common.downloadDocumentExtension", { extension: ".xslx" })}
          </Button>
        </Col>
      </Row>

      <Modal
        type="confirm"
        open={openConfirmModal}
        cancelText={t("common.cancel")}
        confirmText={t("common.yesDelete")}
        onClose={handleCancelConfirmModal}
        onConfirm={handleConfirmedConfirmModal}
      >
        <div>
          {
            programTypeToDelete &&
            <Typography component="h1" variant="largeBold">
              {t("codeBook.programTypes.areYouSureDelete", { name: programTypeToDelete.name })}
            </Typography>
          }

        </div>
      </Modal>
    </>
  );
}

